/**
 * Copyright (c) 2020-2021 Perficient, Inc.
 * Unauthorized copying or distribution of this file prohibited without the express permission of Perficient.
 */
import React, { useState } from "react";
import {
	Grid,
	Button,
	TextField,
	FormControl,
	Input,
	InputLabel,
	Checkbox,
	FormControlLabel,
	Dialog,
	DialogContent,
	Backdrop,
} from "@material-ui/core";
import { isValidExtension } from "../../../../utility/phoneNumberHelpers";
import MuiPhoneInput from "material-ui-phone-number";
import PhoneNumber from "awesome-phonenumber";
import { AVAILABLE_SMS_COUNTRIES } from "../../../../utility/constants";

const agentValidation = (agent, validationError) => {
	if (validationError) {
		return false;
	}
	if (agent.extension && !isValidExtension(agent.extension)) {
		return false;
	}
	return true;
};

/**
 * Modal form used for editing agent
 * @param {Boolean} editAgentModal used to control open/closed state of the modal
 * @param {Set} setEditAgentModal used to set the state of modal
 * @param {Object} agent contain editable details of agent.editAgentModal
 * @param {Function} function for saving the agent data
 * @param {object} connect contain info of agent from amazon connect
 * @param {Boolean} isAgentLoading load the agent details when set to true
 * @param {Set} setAgent assign agent details in state
 */

export const AgentExtensionEdit = ({
	editAgentModal,
	setEditAgentModal,
	agent,
	saveAgentHandler,
	connect,
	isAgentLoading,
	setAgent,
}) => {
	const [validationError, setValidationError] = useState("");
	const phoneType =
		connect.phoneType === "SOFT_PHONE" ? "Soft Phone" : connect.phoneType === "DESK_PHONE" ? "Desk Phone" : "";

	const updateProperty = React.useCallback(
		(prop, value) => {
			setAgent((currentAgent) => ({ ...currentAgent, [prop]: value }));
		},

		[setAgent],
	);

	const extensionError = (extension) => {
		if (extension && !isValidExtension(extension)) {
			return "Only number are allowed.";
		}
		return "";
	};

	const handlePhoneNumber = (prop, value) => {
		const phone = new PhoneNumber(value);
		const number = phone.getNumber();
		if (value === "+1") {
			setValidationError("");
		} else if (!phone.isValid()) {
			setValidationError("Please provide a valid phone number.");
		} else {
			setValidationError("");
			setAgent({ ...agent, [prop]: number });
		}
	};
	const isAgentValid = agent && agentValidation(agent, validationError);
	return (
		<Dialog
			disableAutoFocus
			open={editAgentModal}
			BackdropComponent={Backdrop}
			onClose={() => {
				setEditAgentModal(false);
			}}
			maxWidth="xs"
			fullWidth
		>
			<DialogContent>
				<div>
					<h6 style={{ fontSize: 25, padding: 0, margin: 0, textAlign: "center" }}>Agent Voicemail Settings</h6>
					<>
						<Grid style={{ marginTop: 20 }} container direction={"column"}>
							<FormControl>
								<InputLabel htmlFor="agent-name">Name</InputLabel>
								<Input id="agent-name" disableUnderline readOnly value={connect.firstName + " " + connect.lastName} />
							</FormControl>
							<FormControl>
								<InputLabel htmlFor="agent-email">Email</InputLabel>
								<Input id="agent-email" disableUnderline readOnly value={connect.email || ""} />
							</FormControl>
							<FormControl>
								<InputLabel htmlFor="phone-type">Phone Type</InputLabel>
								<Input id="phone-type" disableUnderline readOnly value={phoneType} />
							</FormControl>
							{phoneType === "Desk Phone" && (
								<FormControl>
									<InputLabel htmlFor="phone-number">Phone Number</InputLabel>
									<Input id="phone-number" disableUnderline readOnly value={connect.phoneNumber} />
								</FormControl>
							)}
						</Grid>
						<div>
							<Grid container direction="row" justifyContent="flex-start">
								<Grid item>
									<FormControlLabel
										control={
											<Checkbox
												name="transcribeVoicemail"
												id="transcribe"
												disabled={isAgentLoading}
												onChange={(e) => {
													updateProperty("transcribeVoicemail", e.target.checked);
												}}
												checked={agent.transcribeVoicemail}
												color="secondary"
											/>
										}
										label="Transcribe"
									/>
								</Grid>
								<Grid item style={{ marginLeft: "30px" }}>
									<FormControlLabel
										control={
											<Checkbox
												name="encryptVoicemail"
												id="encrypt"
												disabled={isAgentLoading}
												onChange={(e) => {
													updateProperty("encryptVoicemail", e.target.checked);
												}}
												checked={agent.encryptVoicemail}
												color="secondary"
											/>
										}
										label="Encrypt"
									/>
								</Grid>
							</Grid>
							<TextField
								name="extension"
								inputProps={{ maxLength: "5" }}
								disabled={isAgentLoading}
								onChange={(e) => {
									updateProperty("extension", e.target.value);
								}}
								value={agent.extension || ""}
								label={"Extension"}
								error={!!extensionError(agent.extension)}
								helperText={extensionError(agent.extension)}
							/>
							<p style={{ paddingTop: "10px" }}>Delivery Options</p>
							<Grid container direction="row" justifyContent="flex-start">
								<Grid item>
									<FormControlLabel
										control={
											<Checkbox
												name="deliverEmail"
												id="deliverEmail"
												onChange={(e) => {
													updateProperty("deliverEmail", e.target.checked);
												}}
												checked={agent.deliverEmail}
												color="secondary"
											/>
										}
										label="Email"
									/>
								</Grid>
								<Grid item style={{ marginLeft: "70px" }}>
									<FormControlLabel
										control={
											<Checkbox
												name="deliverSMS"
												id="deliverSMS"
												onChange={(e) => {
													updateProperty("deliverSMS", e.target.checked);
												}}
												checked={agent.deliverSMS}
												color="secondary"
											/>
										}
										label="SMS"
									/>
								</Grid>
							</Grid>
							{agent.deliverSMS && (
								<>
									<Grid container direction="column" justifyContent="flex-start">
										<label>Phone Number</label>
										<MuiPhoneInput
											placeholder="Phone Number for SMS"
											onChange={(e) => {
												handlePhoneNumber("deliverSMSPhoneNumber", e);
											}}
											defaultCountry={AVAILABLE_SMS_COUNTRIES[0] || "us"}
											disableAreaCodes
											onlyCountries={AVAILABLE_SMS_COUNTRIES}
											countryCodeEditable={false}
											autoFormat
											maxLength={10}
											style={{ width: "65%" }}
											value={agent.deliverSMSPhoneNumber}
										/>
									</Grid>
								</>
							)}
						</div>
						<p style={{ color: "#D8000C" }}>{validationError}</p>
						<Grid container spacing={2} direction="row" justifyContent="flex-end">
							<Grid item>
								<Button
									color="primary"
									variant="outlined"
									onClick={() => {
										setEditAgentModal(false);
									}}
								>
									Cancel
								</Button>
							</Grid>
							<Grid item>
								<Button
									variant="contained"
									disabled={!isAgentValid}
									style={{
										opacity: isAgentValid ? 1 : 0.4,
										backgroundColor: "#cc1f20",
										color: "#fff",
										borderColor: "#cc1f20",
									}}
									onClick={() => {
										saveAgentHandler(agent.agentId, agent);
										setEditAgentModal(false);
									}}
								>
									Save
								</Button>
							</Grid>
						</Grid>
					</>
				</div>
			</DialogContent>
		</Dialog>
	);
};
